import { useCallback, useId, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import type { FilterOption } from '@/api/types'
import { EnvironmentFilterBar } from '@/components/EnvironmentFilterBar'
import { Icon, useDismiss } from '@/design'
import {
  activeDimensionCount,
  COMPARISON_LABELS,
  COMPARISONS,
  DATE_PRESET_LABELS,
  DATE_PRESETS,
  DIMENSION_LABELS,
  DIMENSIONS,
  isIsoDate,
  resolvePeriod,
  type Comparison,
  type DatePreset,
  type Dimension,
} from '@/lib/filters'
import { useGlobalFilters } from '@/lib/useGlobalFilters'

function Options({ options }: { options: readonly FilterOption[] | undefined }) {
  if (!options) return null
  const groups = [...new Set(options.map((option) => option.group ?? ''))]
  if (groups.length <= 1) {
    return options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))
  }
  return groups.map((group) => (
    <optgroup key={group} label={group}>
      {options
        .filter((option) => (option.group ?? '') === group)
        .map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
    </optgroup>
  ))
}

/**
 * The global filter bar. Everything it holds lives in the URL (see
 * `lib/filters.ts`), so every page reads the same state and every link can
 * carry it. Date range and comparison are always visible; the entity and
 * audience dimensions sit behind "Filters", with the active ones shown as
 * removable chips.
 */
export function GlobalFilterBar() {
  const client = useApiClient()
  const { filters, setFilters, reset } = useGlobalFilters()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const panelId = useId()
  const close = useCallback(() => setOpen(false), [])
  useDismiss(ref, open, close)

  const options = useQuery({
    queryKey: ['filter-options', client.kind],
    queryFn: () => client.filterOptions(),
    staleTime: 5 * 60_000,
  })

  const activeCount = activeDimensionCount(filters) + (filters.environments.length > 0 ? 1 : 0)
  const setDimension = (dimension: Dimension, value: string) =>
    setFilters((current) => ({ ...current, dimensions: { ...current.dimensions, [dimension]: value || undefined } }))
  const labelFor = (dimension: Dimension, value: string) =>
    options.data?.[dimension].find((option) => option.value === value)?.label ?? value

  return (
    <div className="ci-filterbar" role="group" aria-label="Global filters">
      <label className="ci-field ci-field--inline">
        <Icon name="clock" size={14} />
        <span className="ci-visually-hidden">Date range</span>
        <select
          value={filters.range}
          onChange={(event) => {
            const range = event.target.value as DatePreset
            if (range === 'custom') {
              const { current } = resolvePeriod(filters, new Date())
              setFilters({ range, from: current.from, to: current.to })
            } else {
              setFilters({ range, from: null, to: null })
            }
          }}
        >
          {DATE_PRESETS.map((preset) => (
            <option key={preset} value={preset}>
              {DATE_PRESET_LABELS[preset]}
            </option>
          ))}
        </select>
      </label>

      {filters.range === 'custom' ? (
        <span className="ci-filterbar__custom">
          <label className="ci-field ci-field--inline">
            <span className="ci-field__label">From</span>
            <input
              type="date"
              value={filters.from ?? ''}
              max={filters.to ?? undefined}
              onChange={(event) => isIsoDate(event.target.value) && setFilters({ from: event.target.value })}
            />
          </label>
          <label className="ci-field ci-field--inline">
            <span className="ci-field__label">To</span>
            <input
              type="date"
              value={filters.to ?? ''}
              min={filters.from ?? undefined}
              onChange={(event) => isIsoDate(event.target.value) && setFilters({ to: event.target.value })}
            />
          </label>
        </span>
      ) : null}

      <label className="ci-field ci-field--inline">
        <span className="ci-field__label">Compare</span>
        <select value={filters.compare} onChange={(event) => setFilters({ compare: event.target.value as Comparison })}>
          {COMPARISONS.map((comparison) => (
            <option key={comparison} value={comparison}>
              {COMPARISON_LABELS[comparison]}
            </option>
          ))}
        </select>
      </label>

      <div className="ci-filterbar__more" ref={ref}>
        <button
          type="button"
          className="ci-button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((value) => !value)}
        >
          <Icon name="filter" size={14} />
          Filters
          {activeCount > 0 ? (
            <span className="ci-button__count">
              {activeCount}
              <span className="ci-visually-hidden"> active</span>
            </span>
          ) : null}
        </button>

        {open ? (
          <div id={panelId} className="ci-popover ci-filterbar__panel" role="group" aria-label="More filters">
            <div className="ci-filterbar__grid">
              {DIMENSIONS.map((dimension) => (
                <label key={dimension} className="ci-field">
                  <span className="ci-field__label">{DIMENSION_LABELS[dimension]}</span>
                  <select
                    value={filters.dimensions[dimension] ?? ''}
                    onChange={(event) => setDimension(dimension, event.target.value)}
                  >
                    <option value="">All</option>
                    <Options options={options.data?.[dimension]} />
                  </select>
                </label>
              ))}
            </div>
            <div className="ci-filterbar__env">
              <EnvironmentFilterBar
                environments={filters.environments}
                onToggle={(environment) =>
                  setFilters((current) => ({
                    ...current,
                    environments: current.environments.includes(environment)
                      ? current.environments.filter((value) => value !== environment)
                      : [...current.environments, environment],
                  }))
                }
                onReset={() => setFilters({ environments: [] })}
              />
            </div>
            <div className="ci-filterbar__actions">
              <button type="button" className="ci-button" data-variant="ghost" onClick={reset}>
                Clear all
              </button>
              <button type="button" className="ci-button" data-variant="primary" onClick={close}>
                Done
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {activeDimensionCount(filters) > 0 ? (
        <ul className="ci-filterbar__chips" aria-label="Active filters">
          {DIMENSIONS.filter((dimension) => filters.dimensions[dimension]).map((dimension) => {
            const value = filters.dimensions[dimension] ?? ''
            return (
              <li key={dimension}>
                <button type="button" className="ci-chip" onClick={() => setDimension(dimension, '')}>
                  <span>
                    {DIMENSION_LABELS[dimension]}: {labelFor(dimension, value)}
                  </span>
                  <Icon name="close" size={12} />
                  <span className="ci-visually-hidden"> (remove filter)</span>
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
